"use client";

import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { MoonIcon, SunIcon } from "./ui/Icons";

export default function Header() {
  const [isDark, setIsDark] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const pathname = usePathname();
  const isHome = pathname === "/";

  useEffect(() => {
    // Initial theme check
    const savedTheme = localStorage.getItem("theme");
    const systemDark = window.matchMedia(
      "(prefers-color-scheme: dark)",
    ).matches;
    if (savedTheme === "dark" || (!savedTheme && systemDark)) {
      setIsDark(true);
      document.documentElement.classList.add("dark");
    } else {
      setIsDark(false);
      document.documentElement.classList.remove("dark");
    }

    // Scroll listener
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleTheme = () => {
    const newIsDark = !isDark;
    setIsDark(newIsDark);
    if (newIsDark) {
      document.documentElement.classList.add("dark");
      localStorage.setItem("theme", "dark");
    } else {
      document.documentElement.classList.remove("dark");
      localStorage.setItem("theme", "light");
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-white/80 dark:bg-[#0a0a0a]/80 backdrop-blur-md border-b border-gray-100 dark:border-gray-800 py-3"
          : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        <Link href="/" className="flex items-center gap-2 group">
          <Image
            src="/logo.png"
            alt="CaratPay Logo"
            width={32}
            height={32}
            priority
            className="rounded-lg transition-transform group-hover:scale-105"
          />
          <span className="text-xl font-bold text-[#6b5a1a] dark:text-[#C9A227]">
            CaratPay
          </span>
        </Link>

        {isHome ? (
          <nav className="hidden md:flex items-center gap-8">
            <a
              href="#features"
              className="text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-[#6b5a1a] dark:hover:text-[#C9A227] transition-colors"
            >
              Features
            </a>
            <a
              href="#about"
              className="text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-[#6b5a1a] dark:hover:text-[#C9A227] transition-colors"
            >
              About
            </a>
            <a
              href="#contact"
              className="text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-[#6b5a1a] dark:hover:text-[#C9A227] transition-colors"
            >
              Contact
            </a>
          </nav>
        ) : null}

        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={toggleTheme}
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 text-[#6b5a1a] dark:text-[#C9A227] transition-colors"
            aria-label="Toggle theme"
          >
            {isDark ? <SunIcon /> : <MoonIcon />}
          </button>

          {isHome ? (
            <button
              type="button"
              className="bg-[#C9A227] text-[#1a1a1a] px-5 py-2 rounded-full font-medium text-sm hover:bg-[#B8941F] transition-all hover:shadow-lg hover:shadow-[#C9A227]/20 active:scale-95"
            >
              Get Started
            </button>
          ) : (
            <Link
              href="/"
              className="bg-[#C9A227] text-[#1a1a1a] px-5 py-2 rounded-full font-medium text-sm hover:bg-[#B8941F] transition-all hover:shadow-lg hover:shadow-[#C9A227]/20 active:scale-95"
            >
              Back Home
            </Link>
          )}
        </div>
      </div>
    </header>
  );
}
